import { useEffect, useMemo } from 'react';
import { useMultiFloorStateStore } from '@/apps/office/stores/MultiFloorStore';
import { useMyFloorStore } from '@/apps/office/stores/myFloorStore';
import { useWorkplaceStore } from '../stores';
import { isRoomFloorWebex } from '../utils/room-floor-place.util';

/** Whether the user's room was on a Webex floor when last determined; avoids flips mid-move. */
let lastActiveMediaIsWebex: boolean | null = null;

export const useLicenseFloorUtils = () => {
  const userProfile = useWorkplaceStore((state) => state.profile);

  const currentFloorId = useMyFloorStore((state) => state.currentFloorId);
  const currentFloorPlace = useMultiFloorStateStore(
    (state) => state.getFloorState(currentFloorId || '')?.place
  );
  const floors = useMultiFloorStateStore((state) => state.floors);

  // The floor being viewed (elevator, carousel) — not necessarily the floor the user is on.
  const isCurrentFloorWebex = () => {
    return currentFloorPlace && currentFloorPlace?.type === 'webex';
  };

  const currentRoomPid = useWorkplaceStore((state) => state.currentRoom?.pid);
  const presenceFloorPid = useMultiFloorStateStore((state) => state.currentFloorPresence);
  const roomFloorIsWebex = useMemo(
    () => isRoomFloorWebex(floors, currentRoomPid, presenceFloorPid),
    [floors, currentRoomPid, presenceFloorPid]
  );

  useEffect(() => {
    if (roomFloorIsWebex !== null) lastActiveMediaIsWebex = roomFloorIsWebex;
    // No room at all (signed out): don't carry the last user's provider into the next session.
    else if (!currentRoomPid) lastActiveMediaIsWebex = null;
  }, [roomFloorIsWebex, currentRoomPid]);

  /** Whether the user's own room is on a Webex floor; use it, not isCurrentFloorWebex, for meeting logic. */
  const isActiveMediaWebex = () =>
    roomFloorIsWebex ?? lastActiveMediaIsWebex ?? !!isCurrentFloorWebex();

  const isCurrentUserWebex = () => {
    return userProfile?.licenses?.some((license) => license.includes('webex'));
  };

  const isCurrentUserWebexFree = () => {
    return userProfile?.licenses?.some((license) => license === 'webex');
  };

  const isCurrentUserWebexPaid = () => {
    return hasPaidWebexLicense(userProfile?.licenses);
  };

  const getCurrentUserLicense = () => {
    return userProfile?.licenses?.[0];
  };

  const isCurrentFloorCcaas = () => {
    return currentFloorPlace && currentFloorPlace?.type === 'ccaas';
  };

  const isCurrentUserCcaasSupervisorOrAdmin = () => {
    return userProfile?.roles.some((role) =>
      ['supervisor', 'administrator', 'owner'].includes(role.role)
    );
  };

  const isCurrentUserCcaasAgent = () => {
    return userProfile?.roles.some((role) => ['agent'].includes(role.role));
  };

  const currentUserHasCcaasFloors = () => {
    if (floors && Object.keys(floors).length > 0) {
      if (Object.values(floors).some((floor) => floor.place?.type === 'ccaas')) {
        return true;
      }
    }
    return false;
  };

  const getAccessibleFloorTypes = (userLicense: string): string[] => {
    const webex = 'webex';
    const jitsiGroup = ['nexgen', 'ccaas'];

    if (userLicense.startsWith(webex)) {
      // Webex license → only Webex floors
      return [webex];
    }

    if (jitsiGroup.includes(userLicense)) {
      // Nexgen or CCaaS license → can enter nexgen, ccaas, or webex
      return [...jitsiGroup, webex];
    }

    // Default: no access
    return [];
  };

  return {
    isCurrentFloorWebex,
    isActiveMediaWebex,
    getAccessibleFloorTypes,
    isCurrentUserWebex,
    getCurrentUserLicense,
    isCurrentFloorCcaas,
    isCurrentUserCcaasSupervisorOrAdmin,
    currentUserHasCcaasFloors,
    isCurrentUserCcaasAgent,
    isCurrentUserWebexFree,
    isCurrentUserWebexPaid,
  };
};

export const hasPaidWebexLicense = (licenses: string[] | undefined): boolean =>
  !!licenses?.some((license) => license === 'webex-meet' || license === 'webex-suite');

export const isFloorPlaceTypeCcaas = (placeType: string | undefined) => {
  return placeType === 'ccaas';
};

/**
 * Is this room an auditorium, per the floor's place data?
 *
 * NGE-681: auditorium attendees (guests included) are rendered inside the meeting
 * stage, so they must not also appear as avatars on the office floor map. That is a
 * property of the ROOM being drawn — deciding it from the viewer's own
 * `meetingStore.isAuditorium` hid guests in every room of the floor as soon as the
 * viewer themselves moved into an auditorium.
 */
export const isAuditoriumRoom = (
  place: { rooms?: { pid: string; type?: string }[] } | undefined | null,
  roomPid: string | undefined
): boolean => {
  if (!roomPid) return false;
  return place?.rooms?.some((room) => room.pid === roomPid && room.type === 'auditorium') ?? false;
};

/**
 * Non-hook twin of `isCurrentUserCcaasSupervisorOrAdmin`, for use outside React components
 * (services, event routers). Reads the same profile from the store.
 */
export const isCurrentUserCcaasSupervisorOrAdminStatic = (): boolean => {
  try {
    const profile = useWorkplaceStore.getState().profile;
    return (
      profile?.roles?.some((role) =>
        ['supervisor', 'administrator', 'owner'].includes(role.role)
      ) ?? false
    );
  } catch (error) {
    console.error('Error checking CCaaS supervisor privileges:', error);
    return false;
  }
};

export const isCurrentUserPlatformAdminOrOwner = (): boolean => {
  try {
    const profile = useWorkplaceStore.getState().profile;

    // Check if user has platform access
    if (profile.hasPlatformAccess) {
      return true;
    }

    // Check if user has owner role in organization
    const hasOwnerRole = profile.roles.some(
      (role) => role.role === 'owner' && role.scope === 'organization'
    );

    return hasOwnerRole;
  } catch (error) {
    console.error('Error checking user privileges:', error);
    return false;
  }
};

/**
 * Check if the current user has platform admin access ONLY
 * This function does NOT include organization owner privileges
 * Use this when you need to restrict access to platform administrators only
 * @returns true if user has platform access, false otherwise
 */
export const isCurrentUserPlatformAdmin = (): boolean => {
  try {
    const profile = useWorkplaceStore.getState().profile;
    return profile.hasPlatformAccess === true;
  } catch (error) {
    console.error('Error checking platform admin privileges:', error);
    return false;
  }
};

/**
 * Check if the current user is an administrator or owner at the organization level
 * @returns true if user is admin or owner, false otherwise
 */
export const isCurrentUserAdminOrOwner = (): boolean => {
  try {
    const profile = useWorkplaceStore.getState().profile;

    // Check if user has admin or owner role in organization
    const hasPrivilegedRole = profile.roles.some(
      (role) =>
        (role.role === 'administrator' || role.role === 'owner') && role.scope === 'organization'
    );

    return hasPrivilegedRole;
  } catch (error) {
    console.error('Error checking user admin/owner privileges:', error);
    return false;
  }
};

/**
 * Check if the current user has a supervisor role (any scope).
 */
export const isCurrentUserSupervisor = (): boolean => {
  try {
    const profile = useWorkplaceStore.getState().profile;
    return profile.roles.some((role) => role.role === 'supervisor');
  } catch (error) {
    console.error('Error checking user supervisor privileges:', error);
    return false;
  }
};
